/**
 * 매장 이용보호자 탭 — 이 매장을 이용하는 보호자(예약이력 또는 최근 1년 유효계약) 수 + 목록.
 *  각 행을 펼치면 반려견별 이용 상세(이용횟수·최근 이용일)를 볼 수 있다.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, ChevronRight, ChevronDown, PawPrint } from 'lucide-react';
import { companyApi } from '@/services/api';
import type { CompanyGuardiansDto } from '@/services/api/members';

export function CompanyGuardiansPanel({ companySeq }: { companySeq: number }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useQuery<CompanyGuardiansDto>({
    queryKey: ['companyGuardians', companySeq],
    queryFn: () => companyApi.getCompanyGuardians(companySeq),
    staleTime: 30 * 1000,
  });

  const toggle = (userSeq: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(userSeq)) next.delete(userSeq);
      else next.add(userSeq);
      return next;
    });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-400">
        이용보호자 불러오는 중…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-red-500">
        이용보호자를 불러오지 못했습니다.
      </div>
    );
  }

  const guardians = data.guardians ?? [];

  return (
    <div className="space-y-4">
      {/* 요약 */}
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-orange-50">
          <Users className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <div className="text-sm text-gray-500">이용보호자</div>
          <div className="text-2xl font-bold text-gray-900">
            {data.totalCount.toLocaleString()}
            <span className="ml-1 text-base font-medium text-gray-500">명</span>
          </div>
        </div>
        <div className="ml-auto text-xs text-gray-400">
          예약 이력 또는 최근 1년 유효계약 보유 기준
        </div>
      </div>

      {/* 목록 */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-gray-500">
                <th className="w-8 px-2 py-2.5" />
                <th className="px-4 py-2.5 font-medium">보호자명</th>
                <th className="px-4 py-2.5 font-medium">연락처</th>
                <th className="px-4 py-2.5 font-medium">이메일</th>
                <th className="px-4 py-2.5 font-medium">이용 반려견</th>
                <th className="px-4 py-2.5 text-right font-medium">이용횟수</th>
                <th className="px-4 py-2.5 font-medium">최근 이용일</th>
              </tr>
            </thead>
            <tbody>
              {guardians.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    이 매장을 이용하는 보호자가 없습니다.
                  </td>
                </tr>
              ) : (
                guardians.map((g) => {
                  const isOpen = expanded.has(g.userSeq);
                  return (
                    <Fragment key={g.userSeq}>
                      <tr
                        className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                        onClick={() => toggle(g.userSeq)}
                      >
                        <td className="px-2 py-2.5 text-gray-400">
                          {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">
                          {g.userName || '-'}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{g.phoneNumber || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-600">{g.email || '-'}</td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {g.petNames || '-'}
                          {g.petCount > 0 && (
                            <span className="ml-1 text-xs text-gray-400">({g.petCount})</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700">
                          {g.reservationCount > 0 ? `${g.reservationCount}회` : '-'}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{g.lastReservAt || '-'}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-gray-100 bg-gray-50/60">
                          <td />
                          <td colSpan={6} className="px-4 py-3">
                            <div className="text-xs font-medium text-gray-500">반려견별 이용 상세</div>
                            <div className="mt-2 space-y-1.5">
                              {(g.pets ?? []).length === 0 ? (
                                <div className="text-xs text-gray-400">반려견 정보가 없습니다.</div>
                              ) : (
                                (g.pets ?? []).map((p) => (
                                  <div
                                    key={p.petSeq}
                                    className="flex items-center gap-3 rounded-md bg-white px-3 py-2 ring-1 ring-gray-100"
                                  >
                                    <PawPrint className="h-4 w-4 shrink-0 text-orange-400" />
                                    <span className="min-w-0 flex-1 truncate font-medium text-gray-800">
                                      {p.petName || '-'}
                                    </span>
                                    <span className="text-gray-600">
                                      이용{' '}
                                      <b className="text-gray-900">
                                        {p.reservationCount > 0 ? `${p.reservationCount}회` : '0회'}
                                      </b>
                                    </span>
                                    <span className="w-28 text-right text-gray-500">
                                      {p.lastReservAt ? `최근 ${p.lastReservAt}` : '이용 없음'}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
